import { Component } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import {
    AuthenticationService,
    EspaceClientService,
} from '@kv-common-components/kx-services';
import { I18nPipe } from '@shared/pipes/i18n.pipe';
import { ContextDossier } from '@kv-common-components/kv-common-components';
import { AssuranceHelperService } from '@shared/services/assurance-helper.service';
import { GarantieTdb } from '@modules/espace-client/tableau-de-bord/garantie-tdb';
import { FormatHelperService } from '@shared/services/format-helper.service';
import { RepriseHelperService } from '@shared/services/reprise-helper.service';
import * as moment from 'moment';
import { PersonneHelperService } from '@shared/services/personne-helper.service';
import { RouterHelperService } from '@shared/services/router-helper.service';
import { StatutDA } from '@shared/services/statut-da';

@Component({
    selector: 'app-tableau-de-bord',
    templateUrl: './tableau-de-bord.component.html',
    styleUrls: [],
})
export class TableauDeBordComponent {
    static readonly keyLabelDashboardProjectReference =
        'dashboard.project.reference';
    static readonly keyLabelDashboardProjectsStatus =
        'dashboard.projects.status';
    static readonly keyLabelDashboardProjectsStatusMessage =
        'dashboard.projects.status.message';
    static readonly keyLabelGlobalUnitMonths = 'global.unit.months';
    static readonly keyLabelDashboardProjectContract =
        'dashboard.project.contract';
    static readonly keyLabelDashboardProjectMontantTotal =
        'dashboard.project.montantTotal';
    static readonly keyLabelDashboardHeaderWelcome = 'dashboard.header.welcome';
    static readonly keyLabelDashboardHeaderAgence = 'dashboard.header.agence';
    static readonly keyLabelDashboardHeaderAgenceDefault =
        'dashboard.header.agence.default';
    static readonly keyLabelDashboardHeaderText = 'dashboard.header.text';

    static readonly keyLabelPropositionRecapGuaranteeExtraFranchise =
        'proposition.recap.guarantee.extra.franchise';
    static readonly keyLabelPropositionRecapGuaranteeExtraDureePriseCharge =
        'proposition.recap.guarantee.extra.dureeprisecharge';

    currentIndexDossier = 0;
    currentIndexPret = 0;
    currentIndexPretOtherItv = 0;
    currentEspaceClient: any;
    listDossier: ContextDossier[];

    showHistoryDossier = false;
    showLemoineModal = false;
    currentHistoryDossier: ContextDossier;

    constructor(
        private logger: NGXLogger,
        private i18nPipe: I18nPipe,
        private authService: AuthenticationService,
        private espaceClientService: EspaceClientService,
        private repriseHelperService: RepriseHelperService,
        private personneHelperService: PersonneHelperService,
        private routerHelperService: RouterHelperService
    ) {
        const user = this.authService.currentUserValue;
        let methodEspaceClient = 'refreshEspaceClient';
        if (
            routerHelperService.previousUrl === '' ||
            routerHelperService.previousUrl.includes('connexion')
        ) {
            methodEspaceClient = 'getrefreshedEspaceClient';
        }

        this.espaceClientService[methodEspaceClient](user.idPersonne).subscribe(
            (espaceClient) => {
                this.currentEspaceClient = espaceClient;
                this.listDossier = this.initializeTableauDeBord(
                    this.currentEspaceClient.personne.idPersonne
                );
                this.openDossierByIndex(0);
            }
        );
    }

    /**
     * Initialise la liste des dossiers a afficher dans le dashboard associes a l emprunteur
     *
     * @param idPersonne : identifiant KY de l emprunteur
     */
    initializeTableauDeBord(idPersonne: string): ContextDossier[] {
        this.logger.info(`initializeTableauDeBord idPersonne : ${idPersonne}`);
        const listDossier: ContextDossier[] = [];

        let contextDossier: ContextDossier = null;
        for (const dossier of this.currentEspaceClient.listeProjetsCredits) {
            contextDossier = new ContextDossier();
            contextDossier.dossier = dossier;
            contextDossier.emprunteur = dossier.listeEmprunteurs.find(
                (itv) => itv.idPersonne === idPersonne
            );
            contextDossier.emprunteur = JSON.parse(
                JSON.stringify(contextDossier.emprunteur)
            );

            contextDossier.emprunteur.labelReference = this.i18nPipe
                .transform(
                    TableauDeBordComponent.keyLabelDashboardProjectReference
                )
                .replace(
                    '%s',
                    contextDossier.emprunteur.idSousDossier.toString()
                );

            //On recherche l'assurance active de l emprunteur ou sa derniere assurance sans suite
            let currentAssurance = contextDossier.emprunteur.assurance;
            if (
                !currentAssurance &&
                contextDossier.emprunteur.listeAssuranceSansSuite &&
                contextDossier.emprunteur.listeAssuranceSansSuite.length > 0
            ) {
                currentAssurance =
                    contextDossier.emprunteur.listeAssuranceSansSuite[0];
            }
            contextDossier.emprunteur.currentAssurance = currentAssurance;
            if (
                contextDossier.emprunteur.listeCodeDaDateCalculee &&
                AssuranceHelperService.getListStatutDaPostCreation()
                    .map((statut) => statut.codeAia)
                    .includes(currentAssurance.statutDA)
            ) {
                const statutDACalculee =
                    contextDossier.emprunteur.listeCodeDaDateCalculee[
                        contextDossier.emprunteur.listeCodeDaDateCalculee
                            .length - 1
                    ].daCode;
                this.logger.debug(
                    'Overwriting contextDossier.emprunteur.assurance.statutDA (',
                    contextDossier.emprunteur.currentAssurance.statutDA,
                    ') with : ',
                    statutDACalculee
                );
                contextDossier.emprunteur.currentAssurance.statutDA =
                    statutDACalculee;
            }
            if (currentAssurance) {
                const statutDAForSSM = ['FC', 'CB', 'FA'];
                let SSM = '';
                if (
                    contextDossier.emprunteur.topSansSelectionMedicale &&
                    statutDAForSSM.includes(currentAssurance.statutDA)
                ) {
                    SSM = '.SSM';
                }

                contextDossier.emprunteur.currentAssurance.labelStatutDA =
                    this.i18nPipe.transform(
                        `${TableauDeBordComponent.keyLabelDashboardProjectsStatus}.${currentAssurance.statutDA}` +
                            SSM
                    );
                contextDossier.emprunteur.currentAssurance.msgStatutDA =
                    this.i18nPipe.transform(
                        `${TableauDeBordComponent.keyLabelDashboardProjectsStatusMessage}.${currentAssurance.statutDA}` +
                            SSM
                    );

                contextDossier.emprunteur.currentAssurance.typeDashboard =
                    AssuranceHelperService.typeDashboardUnknow;
                const statutDA = AssuranceHelperService.getStatutDAfromCodeAia(
                    currentAssurance.statutDA
                );

                this.updateStatutDa(statutDA, contextDossier);
            }

            listDossier.push(contextDossier);
        }

        return listDossier.sort((dossierA, dossierB) =>
            moment(dossierB.emprunteur.dateEffet).diff(
                moment(dossierA.emprunteur.dateEffet)
            )
        );
    }

    /**
     * Prepare les donnees a afficher pour le dossier selectionne par l utilisateur
     *
     * @param index: index du dossier selectionne dans la liste des dossiers du dashboard
     */
    openDossierByIndex(index: number): void {
        this.logger.info(`openDossierByIndex index : ${index}`);
        this.currentIndexDossier = index;
        this.currentIndexPret = 0;

        if (this.listDossier && this.listDossier.length > 0) {
            const dossier = this.listDossier[index];
            FormatHelperService.checkAssurance(dossier);
            const user = this.authService.currentUserValue;
            user.idDossier = dossier.dossier.idProjetCredit;
            user.idPropositionPrincipale =
                dossier.emprunteur.assurance.idPropositionPrincipale;
            this.authService.updateCurrentUser(user);
            if (dossier.listPret.length === 0) {
                this.populateEmprunteur(dossier.emprunteur);
                this.populateListPret(dossier);
            }
        }
    }

    /**
     * Prepare les donnees de l emprunteur a afficher pour le dossier selectionne
     *
     * @param emprunteur: emprunteur du dossier selectionne
     */
    populateEmprunteur(emprunteur: any): void {
        emprunteur.telephone = this.currentEspaceClient.personne.telephone;
        emprunteur.dateNaissanceFormated = FormatHelperService.getDateFormatted(
            emprunteur.dateNaissance,
            'DD/MM/YYYY',
            true
        );

        emprunteur.adresseFormated = '';
        if (this.currentEspaceClient.personne.adresse1) {
            emprunteur.adresseFormated +=
                this.currentEspaceClient.personne.adresse1;
        }
        emprunteur.adresseFormated +=
            ' ' + this.currentEspaceClient.personne.codePostal;
        emprunteur.adresseFormated +=
            ' ' + this.currentEspaceClient.personne.ville;
    }

    /**
     * Prepare les donnees de chaque pret a afficher pour le dossier selectionne
     *
     * @param dossier
     */
    populateListPret(dossier: ContextDossier): void {
        let indexPret = 0;
        let totalAmount = 0;
        for (const itvPret of dossier.dossier.listeEmprunteursPrets) {
            if (
                itvPret.idEmprunteur === dossier.emprunteur.idEmprunteur &&
                itvPret.quotite > 0 &&
                itvPret.codeRole
            ) {
                const pret = dossier.dossier.listePrets.find(
                    (pretTmp) => pretTmp.idPret === itvPret.idPret
                );
                const newPret = JSON.parse(JSON.stringify(pret));

                indexPret++;
                totalAmount += newPret.montant;

                newPret.label = `Prêt ${indexPret}`;
                newPret.labelDuree =
                    newPret.duree +
                    ' ' +
                    this.i18nPipe
                        .transform(
                            TableauDeBordComponent.keyLabelGlobalUnitMonths
                        )
                        .toUpperCase();
                newPret.labelAmount =
                    FormatHelperService.getAmountFormattedNoDigit(
                        newPret.montant
                    );

                if (dossier.emprunteur.currentAssurance) {
                    newPret.labelAssurance = this.i18nPipe
                        .transform(
                            TableauDeBordComponent.keyLabelDashboardProjectContract +
                                '.' +
                                dossier.emprunteur.currentAssurance
                                    .offreSelectionnee.codeProduit
                        )
                        .toUpperCase();
                    newPret.alertContratSurrisqueKey =
                        AssuranceHelperService.getAlertContratBundleKey(
                            dossier.emprunteur.currentAssurance
                        );
                }

                this.populateListGarantieForPret(newPret, dossier);
                this.populateListOtherEmprunteur(newPret, dossier);

                dossier.listPret.push(newPret);
            }
        }

        const totalAmountStr =
            FormatHelperService.getAmountFormattedNoDigit(totalAmount);
        dossier.emprunteur.labelTotalAmount = this.i18nPipe
            .transform(
                TableauDeBordComponent.keyLabelDashboardProjectMontantTotal
            )
            .replace('%s', totalAmountStr);
    }

    /**
     * Prepare les donnees de chaque garantie et sous-garantie a afficher pour le pret du dossier selectionne
     *
     * @param newPret: le pret du dossier selectionne
     * @param dossier: le dossier selectionne dans le dashboard
     */
    populateListGarantieForPret(newPret: any, dossier: ContextDossier): void {
        newPret.listGarantie = [];
        if (
            dossier.emprunteur.currentAssurance &&
            dossier.emprunteur.currentAssurance.offreSelectionnee &&
            dossier.emprunteur.currentAssurance.offreSelectionnee.listeGaranties
        ) {
            for (const gar of dossier.emprunteur.currentAssurance
                .offreSelectionnee.listeGaranties) {
                const garTdb = this.createGarantieTdb(
                    gar,
                    newPret,
                    dossier.emprunteur,
                    false
                );
                if (garTdb) {
                    newPret.listGarantie.push(garTdb);
                    if (gar.listeGaranties) {
                        garTdb.listGarantie = [];
                        for (const subGar of gar.listeGaranties) {
                            const subGarTdb = this.createGarantieTdb(
                                subGar,
                                newPret,
                                dossier.emprunteur,
                                true
                            );
                            if (subGarTdb) {
                                garTdb.listGarantie.push(subGarTdb);
                            }
                        }
                    }
                }
            }
        }
    }

    /**
     * On prepare les donnees d une garantie, ou sous-garantie, a afficher pour le dossier selectionne
     *
     * @param gar: Garantie dont on prepare les donnees
     * @param newPret: le pret associe a la garantie
     * @param emprunteur: l emprunteur du dossier selectionne
     * @param isSubGarantie: indique si il s agit d une garantie ou sous-garantie
     */
    createGarantieTdb(
        gar: any,
        newPret: any,
        emprunteur: any,
        isSubGarantie: boolean
    ): GarantieTdb {
        let garTdb: GarantieTdb;

        const itemQuotite = gar.listeOptionsGarantiePret.find(
            (item) => item.idPret === newPret.idPret
        );
        if (itemQuotite) {
            //On n affiche pas la garantie PTIA car elle est directement associee a la garantie DECES
            //Mais on controle sa couverture selon l age de l emprunteur et la duree du pret
            if (gar.id === 'PTIA') {
                newPret.isDisplayAlertCoveragePtia = this.isEndOfCoveragePtia(
                    emprunteur.dateNaissance,
                    newPret.duree
                );
            } else {
                garTdb = new GarantieTdb();
                garTdb.id = gar.id;
                if (gar.id === 'CDC') {
                    gar.labelQuotite = null;
                } else {
                    garTdb.labelQuotite = `${itemQuotite.quotite} %`;
                }
                garTdb.refused = gar.refusee;

                const keyGarantie = isSubGarantie
                    ? 'subguarantees'
                    : 'guarantees';

                if (emprunteur.currentAssurance) {
                    garTdb.label = this.i18nPipe
                        .transform(
                            `global.${keyGarantie}.${emprunteur.currentAssurance.offreSelectionnee.codeProduit}.${gar.id}`
                        )
                        .toUpperCase();
                    garTdb.tooltip = this.i18nPipe.transform(
                        `global.${keyGarantie}.${emprunteur.currentAssurance.offreSelectionnee.codeProduit}.${gar.id}.info`,
                        ''
                    );
                }

                this.completeGarantieTdbWithOptionsLabels(garTdb, itemQuotite);
            }
        }
        return garTdb;
    }

    /**
     * Complete l objet GarantieTdb avec les libelles associes aux options franchise, duree de prise en charge etc
     *
     * @param garTdb
     * @param itemQuotite
     */
    completeGarantieTdbWithOptionsLabels(
        garTdb: GarantieTdb,
        itemQuotite: any
    ) {
        if (itemQuotite.franchise) {
            garTdb.labelFranchise = this.i18nPipe
                .transform(
                    TableauDeBordComponent.keyLabelPropositionRecapGuaranteeExtraFranchise
                )
                .replace('%s', itemQuotite.franchise.toString())
                .toUpperCase();
        }

        if (itemQuotite.dureePriseEnCharge) {
            garTdb.labelDureePriseEnCharge = this.i18nPipe
                .transform(
                    TableauDeBordComponent.keyLabelPropositionRecapGuaranteeExtraDureePriseCharge
                )
                .replace('%s', itemQuotite.dureePriseEnCharge.toString())
                .toUpperCase();
        }
    }

    /**
     * Prepare la liste des emprunteurs a afficher, associes au pret selectionne
     *
     * @param newPret: Pret du dossier selectionne
     * @param dossier: Dossier selectionne dans le dashboard
     */
    populateListOtherEmprunteur(newPret: any, dossier: ContextDossier): void {
        newPret.listOtherEmprunteur = [];
        for (const itvPret of dossier.dossier.listeEmprunteursPrets) {
            if (
                itvPret.idPret === newPret.idPret &&
                itvPret.idEmprunteur.toString() !==
                    dossier.emprunteur.idEmprunteur.toString() &&
                itvPret.quotite > 0 &&
                itvPret.codeRole
            ) {
                const otherItv = dossier.dossier.listeEmprunteurs.find(
                    (itvTmp) =>
                        itvTmp.idEmprunteur.toString() ===
                        itvPret.idEmprunteur.toString()
                );
                const labelOtherItv = `${otherItv.prenom} ${otherItv.nom}`;

                newPret.listOtherEmprunteur.push(labelOtherItv);
            }
        }
    }

    /**
     * Prepare le libelle de bienvenu du dashboard
     */
    getLabelWelcome(): string {
        const emprunteur = this.currentEspaceClient.personne;
        const labelCivilite = this.personneHelperService.getCivilite(
            emprunteur.codeCivilite
        );
        const nomComplet = `${labelCivilite} ${emprunteur.prenom} ${emprunteur.nom}`;
        return this.i18nPipe
            .transform(TableauDeBordComponent.keyLabelDashboardHeaderWelcome)
            .replace('%s', nomComplet);
    }

    /**
     * Prepare le paragraphe de bienvenu du dashboard
     */
    getParagrapheWelcome(): string {
        const cleI18n = `${
            TableauDeBordComponent.keyLabelDashboardHeaderAgence
        }.${this.currentEspaceClient.idApporteurPrincipal.substring(0, 3)}`;
        let labelApporteur = '';
        if (this.i18nPipe.i18nData[cleI18n]) {
            labelApporteur = this.i18nPipe.transform(cleI18n);
        } else {
            labelApporteur = this.i18nPipe.transform(
                TableauDeBordComponent.keyLabelDashboardHeaderAgenceDefault
            );
        }

        return this.i18nPipe
            .transform(TableauDeBordComponent.keyLabelDashboardHeaderText)
            .replace('%s', labelApporteur);
    }

    /**
     * Verifie si la garantie PTIA sera toujours couverte a la fin de la duree du pret et selon l age de l emprunteur
     * Si a la fin de la duree du pret, l' emprunteur a 70 ans ou plus, la garantie PTIA n est plus couverte
     *
     * @param dateNaissanceStr
     * @param dureePret
     */
    isEndOfCoveragePtia(dateNaissanceStr: string, dureePret: number) {
        const today = new Date();
        const dateNaissance = new Date(dateNaissanceStr);
        let nbMonthAged =
            (today.getFullYear() - dateNaissance.getFullYear()) * 12;
        nbMonthAged -= dateNaissance.getMonth();
        nbMonthAged += today.getMonth();
        nbMonthAged += dureePret;
        return nbMonthAged >= 70 * 12;
    }

    toggleHistoryDossier() {
        this.showHistoryDossier = !this.showHistoryDossier;
    }

    toggleLemoineModal() {
        this.showLemoineModal = !this.showLemoineModal;
    }

    isShowStatutGarantie(
        dossier: ContextDossier,
        garantie: GarantieTdb
    ): boolean {
        return (
            dossier.emprunteur.currentAssurance.typeDashboard ===
                AssuranceHelperService.typeDashboardValid &&
            garantie.refused !== undefined &&
            garantie.refused !== null
        );
    }

    updateStatutDa(statutDA: any, contextDossier: ContextDossier) {
        if (statutDA) {
            contextDossier.emprunteur.currentAssurance.typeDashboard =
                    statutDA.typeDashboard;

            // condition to display button to navigateToResumeStep
            contextDossier.emprunteur.currentAssurance.displayContinueButton =
                this.displayContinueButton(
                    statutDA,
                    contextDossier.emprunteur.currentAssurance
                );
        }
    }

    displayContinueButton(statutDA: StatutDA, currentAssurance: any): boolean {
        const listStatutDaWaitingQSPaper = ['FB', 'FA'];
        const listStatutDaSurrisqueCAER01 = ['SY', 'XO', 'XY', 'XP'];
        const listStatutDaWithoutContinueButton = [
            ...listStatutDaWaitingQSPaper,
            ...listStatutDaSurrisqueCAER01,
            AssuranceHelperService.qsEnAttenteDeSaisiePCconseiller.codeAia,
            AssuranceHelperService.qsEnCoursEtude.codeAia,
            AssuranceHelperService.dossierCompletEnCoursEtude.codeAia,
            AssuranceHelperService.qsNonConformeEnAttenteRetourClientCourrier
                .codeAia,
            AssuranceHelperService.propositionSansSuite.codeAia,
            AssuranceHelperService
                .dossierRefuseSuiteEtudeMedicaleEnCoursEtudeContratSurrisqueN2
                .codeAia,
            AssuranceHelperService
                .dossierRefuseSuiteEtudeMedicaleEnCoursEtudeContratSurrisqueN3
                .codeAia,
            AssuranceHelperService.dossierRefuseSuiteEtudeMedicale.codeAia,
        ];
        return (
            currentAssurance &&
            !(
                listStatutDaWithoutContinueButton.includes(statutDA.codeAia) ||
                AssuranceHelperService.isProjectNotAccepted(currentAssurance) ||
                AssuranceHelperService.isStudySurrisqueLevel2(currentAssurance)
            )
        );
    }

    sortListGrantie(listGarantie: any) {
        return listGarantie.sort((garantieA, garantieB) => {
            if (garantieA.id === 'CDC') {
                return 1;
            } else if (garantieB.id === 'CDC') {
                return -1;
            } else {
                return (
                    garantieA.refused - garantieB.refused ||
                    garantieA.label.localeCompare(garantieB.label)
                );
            }
        });
    }

    showAlertGarantie(listGarantie: any) {
        return listGarantie.some((garantie) => garantie.refused === true);
    }

    refusedGarantie(listGarantie: any) {
        const listSubGaranties = listGarantie.flatMap((garantie) =>
            garantie.listGarantie ? garantie.listGarantie : []
        );
        const allGarenties = listGarantie.concat(listSubGaranties);
        return allGarenties
            .filter((garantie) => garantie.refused === true)
            .sort((a, b) => a.label.localeCompare(b.label));
    }

    refusedGrantieAlertBundle(label: any) {
        return this.i18nPipe
            .transform('dashboard.project.guarantees.refused')
            .replace('%s', label);
    }

    resumeStep(dossier: ContextDossier) {
        this.repriseHelperService.navigateToResumeStep(dossier);
    }
}
